import { Link, Outlet, useRouterState } from "@tanstack/react-router";
import { type KeyboardEvent, type MouseEvent, useRef, useState } from "react";
import { navItems } from "../data/site";
import { useI18n, useLocalizedContent } from "../i18n";
import { Arrow, Brand } from "./Brand";

function Header() {
  const [open, setOpen] = useState(false);
  const brandRef = useRef<HTMLDivElement>(null);
  const adminMenuRef = useRef<HTMLDivElement>(null);
  const adminLinkRef = useRef<HTMLAnchorElement>(null);
  const { locale, setLocale, l } = useI18n();
  const localizedNav = useLocalizedContent(navItems);

  function openAdminMenu(clientX: number, clientY: number) {
    const menu = adminMenuRef.current;
    if (!menu) return;

    const edgeGap = 12;
    const menuWidth = 228;
    const menuHeight = 82;
    const x = Math.min(
      Math.max(clientX, edgeGap),
      window.innerWidth - menuWidth - edgeGap,
    );
    const y = Math.min(
      Math.max(clientY, edgeGap),
      window.innerHeight - menuHeight - edgeGap,
    );

    menu.style.setProperty("--context-x", `${x}px`);
    menu.style.setProperty("--context-y", `${y}px`);
    menu.showPopover();
    requestAnimationFrame(() => adminLinkRef.current?.focus());
  }

  function handleBrandContextMenu(event: MouseEvent<HTMLAnchorElement>) {
    event.preventDefault();
    openAdminMenu(event.clientX, event.clientY);
  }

  function handleBrandKeyDown(event: KeyboardEvent<HTMLAnchorElement>) {
    if (
      event.key !== "ContextMenu" &&
      !(event.shiftKey && event.key === "F10")
    ) {
      return;
    }

    event.preventDefault();
    const rect = brandRef.current?.getBoundingClientRect();
    if (rect) openAdminMenu(rect.left, rect.bottom + 8);
  }

  return (
    <header className="site-header">
      <div className="container site-header__inner">
        <div ref={brandRef} className="brand-context">
          <Brand
            showChinese={false}
            onContextMenu={handleBrandContextMenu}
            onKeyDown={handleBrandKeyDown}
          />
          <div ref={adminMenuRef} className="brand-context-menu" popover="auto">
            <span>ACCSCI CONTROL</span>
            <Link
              ref={adminLinkRef}
              to="/admin"
              onClick={() => adminMenuRef.current?.hidePopover()}
            >
              {l("管理后台", "Admin dashboard")}
              <Arrow direction="up-right" />
            </Link>
          </div>
        </div>
        <nav
          id="mobile-menu"
          className={`main-nav ${open ? "main-nav--open" : ""}`}
          aria-label={l("主导航", "Primary navigation")}
        >
          {localizedNav.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="main-nav__link"
              activeProps={{
                className: "main-nav__link main-nav__link--active",
              }}
              onClick={() => setOpen(false)}
            >
              {item.label}
            </Link>
          ))}
          <button
            type="button"
            className="language-switcher"
            aria-label={l("Switch to English", "切换到中文")}
            onClick={() => setLocale(locale === "zh" ? "en" : "zh")}
          >
            <span aria-hidden="true">{locale === "zh" ? "EN" : "中"}</span>
          </button>
          <Link className="button button--small main-nav__cta" to="/contact">
            {l("提交想法", "Share your idea")} <Arrow />
          </Link>
        </nav>
        <button
          type="button"
          className={`menu-button ${open ? "menu-button--open" : ""}`}
          aria-label={
            open ? l("关闭菜单", "Close menu") : l("打开菜单", "Open menu")
          }
          aria-expanded={open}
          aria-controls="mobile-menu"
          onClick={() => setOpen((value) => !value)}
        >
          <span />
          <span />
        </button>
      </div>
    </header>
  );
}

function Footer() {
  const { l } = useI18n();
  const localizedNav = useLocalizedContent(navItems);
  return (
    <footer className="site-footer">
      <div className="container">
        <div className="site-footer__top">
          <div>
            <Brand inverse />
            <p>
              {l(
                "从想法到工作原型，再从原型走向规模化产品。",
                "From idea to a working prototype. From prototype to production at scale.",
              )}
            </p>
          </div>
          <div className="site-footer__nav">
            <div>
              <span className="footer-label">{l("导航", "Navigation")}</span>
              {localizedNav.map((item) => (
                <Link key={item.to} to={item.to}>
                  {item.label}
                </Link>
              ))}
            </div>
            <div>
              <span className="footer-label">{l("联系", "Contact")}</span>
              <Link to="/contact">{l("提交想法", "Share your idea")}</Link>
              <a href="mailto:hello@accsci.top">hello@accsci.top</a>
            </div>
          </div>
        </div>
        <div className="site-footer__bottom">
          <span>
            © {new Date().getFullYear()} {l("加速科学 AccSci", "AccSci")}
          </span>
          <span>
            {l(
              "AI 软件 · SaaS · 智能硬件",
              "AI Software · SaaS · Smart Hardware",
            )}
          </span>
        </div>
      </div>
    </footer>
  );
}

export function Layout() {
  const { l } = useI18n();
  const isAdmin = useRouterState({
    select: (state) => state.location.pathname.startsWith("/admin"),
  });
  return (
    <>
      <a className="skip-link" href="#main-content">
        {l("跳到主要内容", "Skip to main content")}
      </a>
      {!isAdmin && <Header />}
      <main id="main-content">
        <Outlet />
      </main>
      {!isAdmin && <Footer />}
    </>
  );
}
